// Split VERBATIM from utils/datetime-comprehensive.js (ESM barrel split).
import { DateTime } from 'luxon';
import { BUSINESS_TIMEZONE, nowChicago, parseUTC, utcToChicago } from './dateTimeCore';

export function formatForDisplay(dt, options = {}) {
  const defaultOptions = {
    ...DateTime.DATETIME_MED_WITH_SECONDS,
    ...options
  };
  
  return dt.toLocaleString(defaultOptions);
}

/**
 * Get current time in UTC for database operations
 * @returns {DateTime} - Current time in UTC
 */

export function parseUTCToChicago(utcTimestamp) {
  return utcToChicago(utcTimestamp);
}

/**
 * Format UTC timestamp for display in Chicago timezone (alias for formatForDisplay with utcToChicago)
 * @param {string} utcTimestamp - UTC timestamp from database
 * @param {string} format - Luxon format string
 * @returns {string} - Formatted date string in Chicago timezone
 */

export function formatUTCToChicago(utcTimestamp, format = 'MMM d, yyyy \'at\' h:mm a z') {
  if (!utcTimestamp) return 'No date';
  
  try {
    const chicago = utcToChicago(utcTimestamp);
    return chicago.toFormat(format);
  } catch (error) {
    console.error('Error formatting UTC timestamp to Chicago timezone:', error);
    return 'Invalid date';
  }
}

/**
 * Format date for display in Chicago timezone - handles both Date objects and ISO strings
 * @param {Date|string} dateInput - Date object or ISO string from database
 * @param {string} format - Luxon format string
 * @returns {string} - Formatted date string in Chicago timezone
 */

export function formatDateForDisplay(dateInput, format = 'M/d/yyyy') {
  if (!dateInput) return 'No date';
  
  try {
    let chicago;
    
    if (dateInput instanceof Date) {
      // Handle Date objects from database
      // Check if this is a date-only value (time is 00:00:00 UTC)
      const isoString = dateInput.toISOString();
      
      if (isoString.endsWith('T00:00:00.000Z')) {
        // This is a date-only value, extract just the date part
        const dateOnly = isoString.split('T')[0];
        chicago = DateTime.fromISO(dateOnly);
      } else {
        // This is a datetime value, convert from UTC to Chicago
        chicago = DateTime.fromJSDate(dateInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
      }
    } else if (typeof dateInput === 'string') {
      // Handle ISO strings
      if (dateInput.includes('T')) {
        // Check if this is a date-only value (ends with T00:00:00.000Z)
        if (dateInput.endsWith('T00:00:00.000Z')) {
          // This is a date-only value, extract just the date part
          const dateOnly = dateInput.split('T')[0];
          chicago = DateTime.fromISO(dateOnly);
        } else {
          // ISO string with actual time
          chicago = DateTime.fromISO(dateInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
        }
      } else {
        // Date only string (YYYY-MM-DD) - treat as date-only, no timezone conversion
        chicago = DateTime.fromISO(dateInput);
      }
    } else {
      throw new Error('Invalid date input type');
    }
    
    const result = chicago.toFormat(format);
    
    return result;
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return 'Invalid date';
  }
}

/**
 * Format UTC time for API responses (ISO-8601 with Z)
 * @param {DateTime} dt - DateTime object
 * @returns {string} - ISO string in UTC
 */

export function formatForAPI(dt) {
  return dt.toUTC().toISO();
}

/**
 * Create DateTime from Chicago business hours
 * @param {Object} obj - Object with hour, minute, etc.
 * @param {string} date - Date string (YYYY-MM-DD)
 * @returns {DateTime} - DateTime in Chicago, converted to UTC
 */

export function formatWithTimezoneInfo(dt) {
  const chicago = dt.setZone(BUSINESS_TIMEZONE);
  const utc = dt.toUTC();
  
  return {
    display: chicago.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS),
    timezone: `${chicago.offsetNameShort} / UTC${chicago.offset < 0 ? '' : '+'}${chicago.offset / 60}`,
    utc: utc.toISO(),
    chicago: chicago.toISO(),
    offset: chicago.offset
  };
}

/**
 * Validate database timestamp format
 * @param {string} timestamp - Database timestamp
 * @returns {boolean} - Whether timestamp is valid UTC
 */

export function formatDBTimestampForDisplay(dbTimestamp) {
  try {
    const utc = parseUTC(dbTimestamp);
    const chicago = utc.setZone(BUSINESS_TIMEZONE);
    return chicago.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
}

/**
 * Create accessible time element data
 * @param {string} isoUtc - UTC ISO string
 * @returns {Object} - Data for <time> element
 */

export function formatTimeTo12Hour(timeInput) {
  if (!timeInput) return '';
  
  try {
    let timeString;
    
    // Handle different input types
    if (timeInput instanceof Date) {
      // Convert Date object to Chicago time and extract time
      const chicago = DateTime.fromJSDate(timeInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
      timeString = chicago.toFormat('HH:mm:ss');
    } else if (typeof timeInput === 'string') {
      if (timeInput.includes('T')) {
        // ISO datetime string - convert to Chicago time and extract time
        const chicago = DateTime.fromISO(timeInput, { zone: 'utc' }).setZone(BUSINESS_TIMEZONE);
        timeString = chicago.toFormat('HH:mm:ss');
      } else {
        // Time string (HH:mm:ss or HH:mm)
        timeString = timeInput;
      }
    } else {
      return timeInput.toString();
    }
    
    const [hours, minutes] = timeString.split(':');
    const hour12 = hours === '00' ? 12 : hours > 12 ? hours - 12 : hours;
    const period = hours >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${period}`;
  } catch (error) {
    console.error('Error formatting time to 12-hour:', error);
    return timeInput.toString();
  }
}

/**
 * Format time range for display
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {string} - Formatted time range (e.g., "8:00 PM - 9:30 PM")
 */

export function formatTimeRange(startTime, endTime) {
  if (!startTime || !endTime) return '';
  return `${formatTimeTo12Hour(startTime)} - ${formatTimeTo12Hour(endTime)}`;
}

// Export commonly used formats

export function formatUTCToLocal(utcTimestamp, format = 'MMM d, yyyy \'at\' h:mm a') {
  if (!utcTimestamp) return 'No date';
  
  try {
    let dt;
    
    if (utcTimestamp instanceof Date) {
      dt = DateTime.fromJSDate(utcTimestamp, { zone: 'utc' });
    } else if (typeof utcTimestamp === 'string') {
      dt = DateTime.fromISO(utcTimestamp, { zone: 'utc' });
    } else {
      throw new Error('Invalid timestamp type');
    }
    
    if (!dt.isValid) {
      throw new Error(`Invalid timestamp: ${dt.invalidReason}`);
    }
    
    // Convert to user's local timezone (browser will use system timezone)
    const localDt = dt.toLocal();
    
    return localDt.toFormat(format);
  } catch (error) {
    console.error('Error formatting UTC to local time:', error);
    return 'Invalid date';
  }
}

/**
 * Format time for display in user's local timezone
 * @param {string|Date} utcTimestamp - UTC timestamp
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} - Formatted time string
 */

export function formatTimeToLocal(utcTimestamp, options = {}) {
  if (!utcTimestamp) return 'No time';
  
  try {
    let date;
    
    if (utcTimestamp instanceof Date) {
      date = utcTimestamp;
    } else if (typeof utcTimestamp === 'string') {
      date = new Date(utcTimestamp);
    } else {
      throw new Error('Invalid timestamp type');
    }
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid timestamp');
    }
    
    const defaultOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options
    };
    
    return date.toLocaleTimeString(undefined, defaultOptions);
  } catch (error) {
    console.error('Error formatting time to local:', error);
    return 'Invalid time';
  }
}

export function getChicagoDateString() {
  return nowChicago().toISODate();
}

/**
 * Create a Chicago DateTime from date and time strings
 * @param {string} dateStr - Date string in YYYY-MM-DD format
 * @param {string} timeStr - Time string in HH:mm format
 * @returns {DateTime} DateTime object in Chicago timezone
 */

export function getChicagoTimeString() {
  return nowChicago().toFormat('HH:mm');
}


/**
 * Validate and normalize time input
 * @param {string} timeString - Time string to validate
 * @returns {Object} - Normalized time object
 */

export function getChicagoTimezoneAbbr() {
  return nowChicago().toFormat('z');
}

/**
 * Get current time string in Chicago timezone (HH:mm format)
 * @returns {string} Current time in HH:mm format
 */
